import { useState, useEffect, useCallback } from 'react';

/**
 * Hook pour gérer le tutoriel LCDF
 * 
 * Fonctionnalités :
 * - Stockage personnalisé par utilisateur dans localStorage
 * - Préservation des données lors de la déconnexion
 * - Migration automatique depuis l'ancien système
 * - Callbacks de fin de tutoriel
 * 
 * @param {Function} onTutorialComplete - Callback appelé à la fin du tutoriel
 * @returns {Object} API du tutoriel avec état et actions
 */

// Fonction pour détecter si on est sur mobile
const isMobileDevice = () => {
  return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Fonction pour obtenir la position et le target adaptés selon l'appareil
const getResponsiveStepData = (step) => {
  const isMobile = isMobileDevice();
  
  // Pour les steps 5 et 6 (search-filters et product-actions), centrer sur mobile
  if (isMobile && (step.id === 'search-filters' || step.id === 'product-actions')) {
    const adaptedStep = { ...step, position: 'center' };
    
    // Pour l'étape search-filters sur mobile, cibler uniquement la search bar
    if (step.id === 'search-filters') {
      adaptedStep.target = '.search-form';
    }
    
    return adaptedStep;
  }
  
  return step;
};

const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Bienvenue sur LCDF',
    description: 'Découvrons ensemble votre nouvelle plateforme de commande de fleurs.',
    target: null,
    position: 'center'
  },
  {
    id: 'header-navigation',
    title: 'Navigation principale',
    description: 'Voici votre menu utilisateur. Vous pouvez accéder à votre profil, vos commandes et vous déconnecter.',
    target: '.dropdown.header-right, .mobile-bottom-nav',
    position: 'top'
  },
  {
    id: 'delivery-setup',
    title: 'Configuration de la livraison',
    description: 'ÉTAPE IMPORTANTE : Vous devez d\'abord configurer votre livraison avant de pouvoir commander.',
    target: '.sub-menu button[title*="Livraison"], .sub-menu button:contains("Livraison")',
    position: 'bottom',
    highlight: true
  },
  {
    id: 'categories-panel',
    title: 'Catégories de produits',
    description: 'Naviguez dans les différentes catégories de produits. Vous pouvez redimensionner ce panneau en glissant la barre de séparation.',
    target: '.d-flex.product-panel-container .left-panel, .products .d-flex > div:first-child',
    position: 'right'
  },
  {
    id: 'search-filters',
    title: 'Recherche et filtres',
    description: 'Utilisez la barre de recherche et les filtres pour trouver rapidement vos produits.',
    target: '.header-desktop .search, .products-itemsPerPage',
    position: 'bottom'
  },
  {
    id: 'product-actions',
    title: 'Ajout au panier',
    description: 'Cliquez sur les produits pour voir leurs détails, puis ajoutez-les à votre panier.',
    target: '.products-container .row .col:first-child, .product-card:first-child',
    position: 'top'
  },
  {
    id: 'cart-panel',
    title: 'Votre panier',
    description: 'Voici votre panier. Gérez vos articles et passez commande une fois vos achats terminés.',
    target: '.sub-menu button:contains("Panier")',
    position: 'bottom'
  },
  {
    id: 'account-spaces',
    title: 'Vos espaces personnels',
    description: 'Accédez à "Mon compte" pour gérer vos informations et à "Mes commandes" pour suivre vos commandes.',
    target: '.dropdown-menu',
    position: 'left'
  }
];

const STORAGE_BASE_KEY = 'lcdf_tutorial_state';

// Fonction pour obtenir l'identifiant unique de l'utilisateur
const getUserIdentifier = () => {
  try {
    // Essayer de récupérer l'utilisateur depuis le store User
    const User = require('../services/store/User').default;
    const user = User.getUser();
    
    if (user && user.contact && user.contact.email) {
      return user.contact.email;
    }
    
    if (user && user.id) {
      return `user_${user.id}`;
    }
  } catch (error) {
    // Ignorer les erreurs si le store n'est pas disponible
  }
  
  // Fallback: essayer de récupérer depuis le sessionStorage/localStorage
  const token = sessionStorage.getItem('token');
  const userEmail = sessionStorage.getItem('userEmail') || localStorage.getItem('userEmail');
  const userId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
  
  // Utiliser l'ID utilisateur, l'email, ou le token comme identifiant
  return userId || userEmail || token || 'anonymous';
};

// Fonction pour obtenir la clé de stockage spécifique à l'utilisateur
const getStorageKey = () => {
  const userIdentifier = getUserIdentifier();
  return `${STORAGE_BASE_KEY}_${btoa(userIdentifier).slice(0, 10)}`; // Encoder et tronquer pour éviter les caractères spéciaux
};

export const useTutorial = (onTutorialComplete) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasSeenTutorial, setHasSeenTutorial] = useState(false);

  // Charger l'état depuis localStorage
  useEffect(() => {
    const storageKey = getStorageKey();
    let savedState = localStorage.getItem(storageKey);
    
    // Migration: vérifier s'il existe un ancien tutoriel avec l'ancienne clé
    if (!savedState) {
      const oldState = localStorage.getItem('lcdf_tutorial_state');
      if (oldState) {
        try {
          const parsedOldState = JSON.parse(oldState);
          // Migrer vers la nouvelle structure
          const migratedState = {
            ...parsedOldState,
            userId: getUserIdentifier(),
            lastSeen: new Date().toISOString(),
            version: '1.0',
            migrated: true
          };
          localStorage.setItem(storageKey, JSON.stringify(migratedState));
          localStorage.removeItem('lcdf_tutorial_state'); // Supprimer l'ancienne clé
          savedState = JSON.stringify(migratedState);
          console.log('Tutoriel - Migration effectuée pour l\'utilisateur:', getUserIdentifier());
        } catch (error) {
          console.error('Erreur lors de la migration du tutoriel:', error);
        }
      }
    }
    
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        setHasSeenTutorial(state.hasSeenTutorial || false);
        setIsCompleted(state.isCompleted || false);
        setCurrentStep(state.currentStep || 0);
        
        // Log pour debugging (à retirer en production)
        console.log('Tutoriel - État chargé pour l\'utilisateur:', {
          user: getUserIdentifier(),
          hasSeenTutorial: state.hasSeenTutorial,
          isCompleted: state.isCompleted,
          lastSeen: state.lastSeen,
          migrated: state.migrated || false
        });
      } catch (error) {
        console.error('Erreur lors du chargement de l\'état du tutoriel:', error);
      }
    }
  }, []);

  // Sauvegarder l'état dans localStorage
  const saveState = useCallback((state) => {
    const storageKey = getStorageKey();
    const enhancedState = {
      ...state,
      userId: getUserIdentifier(),
      lastSeen: new Date().toISOString(),
      version: '1.0', // Version du tutoriel
      sessionCount: (state.sessionCount || 0) + 1
    };
    
    localStorage.setItem(storageKey, JSON.stringify(enhancedState));
    
    // Log pour debugging (à retirer en production)
    console.log('Tutoriel - État sauvegardé:', enhancedState);
  }, []);

  // Démarrer le tutoriel
  const startTutorial = useCallback(() => {
    setIsActive(true);
    setCurrentStep(0);
    setIsCompleted(false);
  }, []);

  // Arrêter le tutoriel
  const stopTutorial = useCallback(() => {
    setIsActive(false);
    const newState = {
      hasSeenTutorial: true,
      isCompleted: isCompleted,
      currentStep: currentStep
    };
    setHasSeenTutorial(true);
    saveState(newState);
  }, [isCompleted, currentStep, saveState]);

  // Aller à l'étape suivante
  const nextStep = useCallback(() => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      
      const newState = {
        hasSeenTutorial: hasSeenTutorial,
        isCompleted: false,
        currentStep: newStep
      };
      saveState(newState);
    } else {
      // Tutoriel terminé
      setIsCompleted(true);
      setIsActive(false);
      const newState = {
        hasSeenTutorial: true,
        isCompleted: true,
        currentStep: TUTORIAL_STEPS.length - 1
      };
      setHasSeenTutorial(true);
      saveState(newState);
      
      // Appeler le callback de fin de tutoriel si fourni
      if (onTutorialComplete) {
        setTimeout(() => onTutorialComplete(), 500); // Délai pour laisser l'animation se terminer
      }
    }
  }, [currentStep, hasSeenTutorial, saveState, onTutorialComplete]);

  // Aller à l'étape précédente
  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      
      const newState = {
        hasSeenTutorial: hasSeenTutorial,
        isCompleted: false,
        currentStep: newStep
      };
      saveState(newState);
    }
  }, [currentStep, hasSeenTutorial, saveState]);

  // Aller à une étape spécifique
  const goToStep = useCallback((stepIndex) => {
    if (stepIndex >= 0 && stepIndex < TUTORIAL_STEPS.length) {
      setCurrentStep(stepIndex);
      
      const newState = {
        hasSeenTutorial: hasSeenTutorial,
        isCompleted: false,
        currentStep: stepIndex
      };
      saveState(newState);
    }
  }, [hasSeenTutorial, saveState]);

  // Réinitialiser le tutoriel
  const resetTutorial = useCallback(() => {
    setCurrentStep(0);
    setIsCompleted(false);
    setHasSeenTutorial(false);
    setIsActive(false);
    
    const storageKey = getStorageKey();
    localStorage.removeItem(storageKey);
    
    console.log('Tutoriel - État réinitialisé pour l\'utilisateur:', getUserIdentifier());
  }, []);

  // Fonction utilitaire pour nettoyer tous les tutoriels (admin/debug)
  const clearAllTutorialData = useCallback(() => {
    const keys = Object.keys(localStorage);
    const tutorialKeys = keys.filter(key => key.startsWith(STORAGE_BASE_KEY));
    
    tutorialKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log(`Tutoriel - Nettoyage de ${tutorialKeys.length} entrées de tutoriel`);
  }, []);

  // Vérifier si l'utilisateur devrait voir le tutoriel automatiquement
  const shouldShowTutorial = useCallback(() => {
    return !hasSeenTutorial && !isCompleted;
  }, [hasSeenTutorial, isCompleted]);

  // Créer currentStepData avec position et target responsifs
  const currentStepData = TUTORIAL_STEPS[currentStep] ? 
    getResponsiveStepData(TUTORIAL_STEPS[currentStep]) : null;

  return {
    // État
    isActive,
    currentStep,
    isCompleted,
    hasSeenTutorial,
    
    // Données
    steps: TUTORIAL_STEPS,
    currentStepData,
    totalSteps: TUTORIAL_STEPS.length,
    
    // Actions
    startTutorial,
    stopTutorial,
    nextStep,
    prevStep,
    goToStep,
    resetTutorial,
    clearAllTutorialData,
    shouldShowTutorial,
    
    // Utilitaires
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === TUTORIAL_STEPS.length - 1,
    progress: ((currentStep + 1) / TUTORIAL_STEPS.length) * 100
  };
};

export default useTutorial;